// src/pages/SetAddress.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ProgressTracker from "../components/ProgressTracker"; // ✅ Add this import

const API_WRK = process.env.REACT_APP_API_WRK;

const SetAddress = () => {
  const navigate = useNavigate();
  const workerId = localStorage.getItem("workerId");

  const [autocompleteInput, setAutocompleteInput] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  useEffect(() => {
    if (!workerId) {
      navigate("/worker-login");
      return;
    }

    const initAutocomplete = () => {
      const input = document.getElementById("autocompleteAddress");
      if (!input || !window.google?.maps) return;

      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ["geocode"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.address_components || !place.geometry) return;

        const getComponent = (type) =>
          place.address_components.find((c) => c.types.includes(type))?.long_name || "";

        const fullStreet = `${getComponent("street_number")} ${getComponent("route")}`.trim();

        setAutocompleteInput(place.formatted_address);
        setStreet(fullStreet);
        setCity(getComponent("locality") || getComponent("sublocality") || "");
        setProvince(getComponent("administrative_area_level_1"));
        setPostalCode(getComponent("postal_code"));
        setLatitude(place.geometry.location.lat());
        setLongitude(place.geometry.location.lng());
      });
    };

    if (window.google?.maps) {
      initAutocomplete();
    } else {
      const interval = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(interval);
          initAutocomplete();
        }
      }, 300);
      return () => clearInterval(interval);
    }
  }, [workerId, navigate]);

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("❌ Geolocation not supported by your browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
          if (status === "OK" && results[0]) {
            const place = results[0];
            const getComponent = (type) =>
              place.address_components.find((c) => c.types.includes(type))?.long_name || "";

            const fullStreet = `${getComponent("street_number")} ${getComponent("route")}`.trim();

            setAutocompleteInput(place.formatted_address);
            setStreet(fullStreet);
            setCity(getComponent("locality") || getComponent("sublocality") || "");
            setProvince(getComponent("administrative_area_level_1"));
            setPostalCode(getComponent("postal_code"));
            setLatitude(latitude);
            setLongitude(longitude);
          } else {
            alert("❌ Failed to fetch address from your location.");
          }
        });
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("❌ Unable to retrieve your location.");
      }
    );
  };

  const handleSave = async () => {
    if (!street || !city || !province || !postalCode || !latitude || !longitude) {
      alert("⚠️ Please complete all required address fields.");
      return;
    }

    const fullAddress = `${street}, ${city}, ${province}, ${postalCode}`;

    try {
      await axios.post(`${API_WRK}/update-profile`, {
        workerId,
        address: fullAddress,
        street,
        city,
        province,
        postalCode,
        latitude,
        longitude,
      });

      localStorage.setItem("workerAddress", fullAddress);
      localStorage.setItem("workerCity", city);
      localStorage.setItem("workerProvince", province);
      localStorage.setItem("workerLat", latitude);
      localStorage.setItem("workerLng", longitude);

      navigate("/upload-documents");
    } catch (err) {
      console.error("Error saving address:", err);
      alert(err?.response?.data?.message || "❌ Failed to save address.");
    }
  };

  return (
    <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-indigo-100 via-white to-indigo-200 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-xl space-y-6">
        {/* ✅ Add Progress Bar here */}
        <ProgressTracker currentStep={2} />

        <h1 className="text-2xl font-extrabold text-indigo-700 text-center">
          Set Your Address
        </h1>
        <p className="text-center text-gray-600">
          This helps us show jobs near your location.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Start typing your address:
          </label>
          <input
            id="autocompleteAddress"
            type="text"
            placeholder="Type to autocomplete..."
            value={autocompleteInput}
            onChange={(e) => setAutocompleteInput(e.target.value)}
            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
          <button
            onClick={handleUseMyLocation}
            className="mt-2 bg-indigo-500 text-white py-2 px-4 rounded-xl hover:bg-indigo-600 text-sm transition"
          >
            📍 Use My Location
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Street:</label>
          <input
            type="text"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">City:</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Province:</label>
            <input
              type="text"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code:</label>
          <input
            type="text"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>

        <button
          onClick={handleSave}
          className="w-full bg-indigo-600 text-white py-3 rounded-xl hover:bg-indigo-700 text-lg font-semibold transition"
        >
          Save & Continue
        </button>
      </div>
    </div>
  );
};

export default SetAddress;
